import React from "react";
import {
  GoogleReCaptchaProvider,
  GoogleReCaptcha,
} from "react-google-recaptcha-v3";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { compose } from "redux";
import cookie from "js-cookie";

import { appleLogin, googleLogin, register } from "../../../store/Actions/authActions";
import AuthLayout, { Icon, SocialSignIn } from "../AuthLayout";
import { hasPasswordErrors, isValidEmail, passwordErrors } from "../../../utils/password";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Le formulaire ne collecte que le numéro national (le design affiche +32 en
 * préfixe fixe) : on reconstitue le format international attendu par le back.
 */
const toInternational = (raw) => {
  const trimmed = String(raw || "").trim();

  if (!trimmed) {
    return "";
  }

  if (trimmed.startsWith("+")) {
    return trimmed.replace(/\s+/g, "");
  }

  return `+32${trimmed.replace(/\s+/g, "").replace(/^0+/, "")}`;
};

/** Les trois barres du design : longueur, chiffre, puis variété. */
const passwordScore = (password) => {
  if (!password) {
    return 0;
  }

  return [
    password.length >= 8,
    /\d/.test(password),
    /[A-Z]/.test(password) || /[^A-Za-z0-9]/.test(password),
  ].filter(Boolean).length;
};

class PersonalDetail extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      firstName: "",
      lastName: "",
      phone_number: "",
      email: "",
      password: "",
      passwordConfirmation: "",
      termsConditions: false,
      passwordShown: false,
      confirmationShown: false,
      loading: false,
      success: false,
      errors: {},
      serverError: null,
      token: "",
      refreshReCaptcha: false,
    };
  }

  setTokenFunc = (token) => {
    this.setState({ token });
  };

  handleChange = (event) => {
    const { name, value } = event.target;

    this.setState((prev) => {
      const errors = { ...prev.errors, [name]: null };

      if (name === "email" && value && !EMAIL_PATTERN.test(value)) {
        errors.email = "Please enter a valid email";
      }

      if (name === "password" || name === "passwordConfirmation") {
        const password = name === "password" ? value : prev.password;
        const confirmation =
          name === "passwordConfirmation" ? value : prev.passwordConfirmation;

        errors.password = null;
        errors.passwordConfirmation =
          confirmation && confirmation !== password
            ? "Passwords do not match"
            : null;
      }

      return { [name]: value, errors };
    });
  };

  toggleTerms = () => {
    this.setState((prev) => ({ termsConditions: !prev.termsConditions }));
  };

  isComplete = () => {
    const {
      firstName,
      lastName,
      phone_number,
      email,
      password,
      passwordConfirmation,
      termsConditions,
    } = this.state;

    return (
      termsConditions &&
      firstName !== "" &&
      lastName !== "" &&
      phone_number !== "" &&
      email !== "" &&
      password !== "" &&
      passwordConfirmation !== "" &&
      password === passwordConfirmation
    );
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    this.setState({ loading: true, serverError: null });

    const postData = {
      first_name: this.state.firstName,
      last_name: this.state.lastName,
      phone_number: toInternational(this.state.phone_number),
      email: this.state.email.trim(),
      password: this.state.password,
      password_confirmation: this.state.passwordConfirmation,
      recaptcha_token: this.state.token,
      locale:
        cookie.get("i18next") === "fn"
          ? "fr"
          : cookie.get("i18next") === "du"
          ? "nl"
          : "en",
    };
    /* Le serveur n'est appelé qu'avec un formulaire valide ; mêmes règles de
       mot de passe que le reste de l'app (utils/password). */
    const pwd = passwordErrors({
      password: postData.password,
      confirmation: postData.password_confirmation,
    });
    const errors = {
      email: isValidEmail(postData.email) ? null : "Enter a valid email address.",
      password: pwd.password,
      passwordConfirmation: pwd.confirmation,
    };

    if (errors.email || hasPasswordErrors(pwd)) {
      this.setState({ errors, loading: false });
      return;
    }

    try {
      const response = await this.props.dispatch(register(postData, this.props));

      if (!response) {
        this.setState({ serverError: this.props?.msgs, loading: false });
        return;
      }

      this.setState({ loading: false, success: true, errors: {} });
      this.props.history.push("/use-wosh", [
        { prevPath: this.props.history.location.pathname },
      ]);
    } catch (err) {
      this.setState((prev) => ({
        serverError: this.props?.msgs,
        loading: false,
        refreshReCaptcha: !prev.refreshReCaptcha,
      }));
    }
  };

  renderField(name, { label, type = "text", placeholder, icon, reveal }) {
    const { errors } = this.state;
    const shown = reveal ? this.state[reveal] : false;

    return (
      <>
        <label className="wosh-auth__label" htmlFor={`signup-${name}`}>
          {label}
        </label>
        <div
          className={`wosh-auth__field${
            errors[name] ? " wosh-auth__field--invalid" : ""
          }`}
        >
          {icon && <Icon name={icon} />}
          <input
            required
            id={`signup-${name}`}
            name={name}
            type={reveal && shown ? "text" : type}
            placeholder={placeholder}
            value={this.state[name]}
            onChange={this.handleChange}
          />
          {reveal && (
            <button
              type="button"
              className={`wosh-auth__reveal${
                shown ? " wosh-auth__reveal--on" : ""
              }`}
              onClick={() => this.setState((prev) => ({ [reveal]: !prev[reveal] }))}
              aria-label={this.props.t("Show/Hide Password")}
            >
              <Icon name="i-eye" />
            </button>
          )}
        </div>
        {errors[name] && (
          <p className="wosh-auth__error">{this.props.t(errors[name])}</p>
        )}
      </>
    );
  }

  render() {
    const { t } = this.props;
    const { loading, success, termsConditions, serverError } = this.state;
    const submitting = loading && !success;
    const score = passwordScore(this.state.password);

    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY}
      >
        <AuthLayout
          t={t}
          wide
          title={t("Three minutes, and you never do laundry again.")}
          lede={t(
            "Create your account, add an address, pick a slot. We handle the rest."
          )}
          variant="signup"
          eyebrow={t("New customer")}
          steps={[
            t("Your account and your address"),
            t("Connect with your favorite laundry place"),
            t("Enjoy your free time"),
          ]}
          activeStep={1}
        >
          <h1 className="wosh-auth__title">{t("Create your account")}</h1>
          <p className="wosh-auth__lede">
            {t("Already a customer?")}{" "}
            <Link to="/sign-in">{t("Sign-in")}</Link>
          </p>

          {serverError && (
            <div className="wosh-auth__alert">{t(serverError)}</div>
          )}

          <form onSubmit={this.handleSubmit}>
            <div className="wosh-auth__row wosh-auth__gap">
              <div>
                {this.renderField("firstName", {
                  label: t("First Name"),
                  placeholder: "Camille",
                })}
              </div>
              <div>
                {this.renderField("lastName", {
                  label: t("Last Name"),
                  placeholder: "Dubois",
                })}
              </div>
            </div>

            <div className="wosh-auth__gap">
              {this.renderField("email", {
                label: t("Email address"),
                type: "email",
                placeholder: "camille@exemple.be",
                icon: "i-mail",
              })}
            </div>

            <label className="wosh-auth__label" htmlFor="signup-phone">
              {t("Phone")}
            </label>
            <div className="wosh-auth__field">
              <span className="wosh-auth__prefix">+32</span>
              <span className="wosh-auth__divider-v" />
              <input
                required
                id="signup-phone"
                name="phone_number"
                type="tel"
                placeholder="470 12 34 56"
                value={this.state.phone_number}
                onChange={this.handleChange}
              />
            </div>
            <p className="wosh-auth__hint">
              {t("So the driver can let you know they are on their way.")}
            </p>

            <div className="wosh-auth__row wosh-auth__gap-top">
              <div>
                {this.renderField("password", {
                  label: t("Password"),
                  type: "password",
                  placeholder: "••••••••",
                  reveal: "passwordShown",
                })}
              </div>
              <div>
                {this.renderField("passwordConfirmation", {
                  label: t("Confirmation"),
                  type: "password",
                  placeholder: "••••••••",
                  reveal: "confirmationShown",
                })}
              </div>
            </div>

            <div className="wosh-auth__meter">
              {[0, 1, 2].map((index) => (
                <span key={index} className={index < score ? "is-on" : ""} />
              ))}
            </div>
            <p className="wosh-auth__hint wosh-auth__hint--flush">
              {t("At least 6 characters.")}
            </p>

            <div
              className={`wosh-auth__check${
                termsConditions ? " wosh-auth__check--on" : ""
              }`}
            >
              <button
                type="button"
                role="checkbox"
                aria-checked={termsConditions}
                aria-labelledby="signup-terms-label"
                className="wosh-auth__check-box"
                onClick={this.toggleTerms}
              >
                <Icon name="i-check" />
              </button>
              <span className="wosh-auth__check-text" id="signup-terms-label">
                {t("I accept the")}{" "}
                <a
                  href="https://www.wosh.be/legal/terms-conditions"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("terms & conditions")}
                </a>{" "}
                {t("and the")}{" "}
                <a
                  href="https://www.wosh.be/legal/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("privacy policy")}
                </a>
                .
              </span>
            </div>

            <GoogleReCaptcha
              action="verify"
              onVerify={this.setTokenFunc}
              refreshReCaptcha={this.state.refreshReCaptcha}
            />

            <button
              type="submit"
              className="wosh-auth__cta"
              disabled={!this.isComplete() || submitting}
            >
              {submitting && <span className="wosh-auth__spinner" />}
              {submitting ? t("processing") : t("Create my account")}
            </button>
          </form>

          <div className="wosh-auth__or">
            <span>{t("or")}</span>
          </div>

          <SocialSignIn
            split
            t={t}
            onGoogle={(response) => this.props.dispatch(googleLogin(response))}
            onApple={(authorization) =>
              this.props.dispatch(appleLogin(authorization))
            }
          />
        </AuthLayout>
      </GoogleReCaptchaProvider>
    );
  }
}

const mapStateToProps = (state) => ({
  msgs: state?.auth?.error,
});

export default compose(
  withTranslation(),
  connect(mapStateToProps)
)(PersonalDetail);
